/**
 * NJMMISMFAChallengeBanner — top-of-app banner shown when NJMMIS
 * has paused a Playwright submission on an MFA challenge.
 *
 * Behaviour:
 *   - Polls /api/njmmis/mfa-challenge/active every 5s while mounted.
 *   - When a challenge appears, renders a 6-digit OTP input + Submit.
 *   - On successful submission shows "Submission resuming…" briefly,
 *     then clears.
 *
 * Auth: relies on the JWT token passed by the parent (App.js).
 * No-op when the token is missing or the user's role isn't billing.
 *
 * See /app/memory/NJMMIS_MFA_INTERCEPT_PLAN.md.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 5000;

const NJMMISMFAChallengeBanner = ({ token }) => {
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const pollRef = useRef(null);

  const fetchActive = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(
        `${API_URL}/api/njmmis/mfa-challenge/active`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (r.data?.active) {
        setChallenge(r.data);
      } else {
        // Only clear if we don't have a freshly-submitted success banner up.
        if (!success) setChallenge(null);
      }
    } catch { /* silent — banner stays in its last state */ }
  }, [token, success]);

  useEffect(() => {
    if (!token) return;
    fetchActive();
    pollRef.current = setInterval(fetchActive, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [token, fetchActive]);

  const submit = async () => {
    if (!challenge?.event_id) return;
    const code = otp.trim();
    if (!/^\d{4,10}$/.test(code)) {
      setError('Enter the verification code (digits only).');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/api/njmmis/mfa-challenge/respond`,
        { event_id: challenge.event_id, otp: code },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess(true);
      setOtp('');
      // Hide the success banner after 8s.
      setTimeout(() => {
        setSuccess(false);
        setChallenge(null);
      }, 8000);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not submit code.';
      setError(typeof detail === 'string' ? detail : 'Could not submit code.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) return null;
  if (!challenge && !success) return null;

  if (success) {
    return (
      <div
        data-testid="njmmis-mfa-success-banner"
        className="w-full bg-emerald-50 border-b border-emerald-200 px-4 py-3 flex items-center gap-3"
      >
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        <div className="text-sm text-emerald-900">
          Verification code received. Your NJMMIS submission is resuming automatically.
        </div>
      </div>
    );
  }

  const target = challenge?.delivery_target_masked || 'your provider phone';

  return (
    <div
      data-testid="njmmis-mfa-challenge-banner"
      className="w-full bg-amber-50 border-b border-amber-300 px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-amber-900">
            NJMMIS submission paused — verification code needed
          </div>
          <div className="text-xs text-amber-800 mt-0.5">
            NJMMIS just sent a code to <span className="font-mono">{target}</span>.
            Enter the 6-digit code below to resume your claim submission.
          </div>
          <div className="flex gap-2 items-center mt-2">
            <input
              data-testid="njmmis-mfa-otp-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={10}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              placeholder="123456"
              className="font-mono text-base border border-amber-300 rounded px-3 py-1.5 w-36 focus:outline-none focus:ring-2 focus:ring-amber-400"
              disabled={submitting}
            />
            <button
              data-testid="njmmis-mfa-submit-button"
              onClick={submit}
              disabled={submitting || otp.length < 4}
              className="bg-amber-700 hover:bg-amber-800 disabled:bg-amber-300 text-white text-sm font-semibold px-4 py-1.5 rounded inline-flex items-center gap-2"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Submit
            </button>
            {error && (
              <span data-testid="njmmis-mfa-error" className="text-xs text-red-700">
                {error}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NJMMISMFAChallengeBanner;
